import { infiniteQueryOptions, queryOptions, useInfiniteQuery } from "@tanstack/react-query";
import api from "~/shared/api/core/api";
import { Params } from "~/shared/api/types/params";
import { PaginatedResponse } from "~/shared/types/pagination.types";
import { addFullName, addFullNameToDataItems } from "~/shared/util/addFullNameToDataItems";
import { createInfinitePaginationConfig } from "~/shared/util/createInfinitePaginationConfig";
import { USER_ACCESS } from "../users/users.constants";

export const STAFF_QUERY_KEYS = {
  all: ["staff"] as const,
  list: (params: Params) => [...STAFF_QUERY_KEYS.all, "list", params] as const,
  detail: (id: number | null) => [...STAFF_QUERY_KEYS.all, "detail", id] as const,
};

export const STAFF_QUERY_OPTIONS = {
  infiniteList: (params: { [key: string]: unknown }) => {
    const paginationConfig = createInfinitePaginationConfig(params, 20);

    return infiniteQueryOptions({
      queryKey: STAFF_QUERY_KEYS.list({
        params: paginationConfig.keyParams,
      }),
      queryFn: async ({ pageParam = 1, signal }) => {
        const response = await api.get<PaginatedResponse<StaffMemberResponse>>(`/staff`, {
          params: paginationConfig.queryFnParams(pageParam),
          signal,
        });

        return response.data;
      },
      select: addFullNameToDataItems,
      ...paginationConfig.opts,
    });
  },
  staffList: (params: Params) => {
    return queryOptions({
      queryKey: STAFF_QUERY_KEYS.list(params),
      queryFn: async ({ signal }) => {
        const response = await api.get<PaginatedResponse<StaffMemberResponse>>("/staff", {
          ...params,
          signal,
        });

        return response.data;
      },
      select: (data): PaginatedResponse<StaffMember> => {
        return {
          ...data,
          items: addFullName(data?.items),
        };
      },
    });
  },
  detail: (staffId: number) =>
    queryOptions({
      queryKey: STAFF_QUERY_KEYS.detail(staffId),
      queryFn: async ({ signal }) => {
        const response = await api.get<StaffMemberResponse>(`/staff/${staffId}`, {
          signal,
        });

        return response.data;
      },
    }),
};

type StaffRoles = Exclude<keyof typeof USER_ACCESS, "waitlist" | "participant">;

export interface StaffMemberResponse {
  id: number;
  roles: StaffRoles[];
  active: boolean;
  previouslyActivated: boolean;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  slackUid: string;
  headshot?: {
    location: string;
  };
  status: "Active" | "Pending" | "None";
  createdAt: string;
}

export interface StaffMember extends StaffMemberResponse {
  name: string;
}

export function useInfiniteStaffFilter(params: { [key: string]: unknown }) {
  return useInfiniteQuery(STAFF_QUERY_OPTIONS.infiniteList(params));
}
